import React, { useCallback, useEffect, useRef, useState } from 'react'

import { eventsCollection } from "../utils/firebase";
import { CalendarDaysIcon, ChevronRight, Clock3Icon, WarehouseIcon } from 'lucide-react';
import { formatFirestoreTimestamp, formatFirestoreTimestampToTime } from "../utils/common"
import { ADMIN_TABLE_FETCH_SIZE, eventStatusTooltipMapping } from "../constants";
import useDebounce from '../hooks/use-debounce';
import { getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import { CREATE_EVENT_PATH, MANAGE_EVENT_PATH } from '../App';

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [sort, setSort] = useState("dateTime");
  const [order, setOrder] = useState("desc");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const size = ADMIN_TABLE_FETCH_SIZE;

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  // A ref for the IntersectionObserver's sentinel element
  const observerRef = useRef(null);

  // Use a ref for the loading flag to avoid triggering re-creations of fetchData
  const loadingRef = useRef(false);

  const handleCreate = () => {
    window.open(CREATE_EVENT_PATH, "_self")
  }

  const handleRowClick = (event) => {
    window.open(`${MANAGE_EVENT_PATH}/${event?.id ?? ""}`, "_self")
  }

  const buildQuery = (reset = false) => {
    const constraints = [];

    if (debouncedSearchTerm.trim() !== "") {
      constraints.push(where("title", ">=", debouncedSearchTerm));
      constraints.push(where("title", "<=", debouncedSearchTerm + '\uf8ff'));

      constraints.push(orderBy("title", "asc"));
    } else {
      constraints.push(orderBy(sort, order));
    }

    constraints.push(limit(size));

    if (!reset && !!lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    return query(eventsCollection, ...constraints);
  }

  const fetchData = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const q = buildQuery(reset);
      const snapshot = await getDocs(q);
      const fetchedEvents = snapshot?.docs?.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
        }

      }) ?? [];

      setEvents((prev) => {
        if (reset) {
          return fetchedEvents;
        } else {
          const arr = [
            ...prev,
            ...fetchedEvents,
          ];
          const seenIds = new Set();
          const uniqueData = arr.filter(item => {
            if (seenIds.has(item.id)) {
              return false;
            }
            seenIds.add(item.id);
            return true;
          });
          return uniqueData;
        }
      });

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      setLastDoc(lastVisible);

      setHasMore(snapshot.docs.length === size);

    } catch (error) {
      console.error('Error fetching events data: ', error);
    }
    loadingRef.current = false;
    setLoading(false);
  }, [lastDoc, sort, order, debouncedSearchTerm]);


  useEffect(() => {
    setEvents([]);
    setLastDoc(null);
    setHasMore(true);
    fetchData(true);
  }, [sort, order, debouncedSearchTerm]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting && hasMore && !loadingRef.current) {
        fetchData();
      }
    });

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }
    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasMore, fetchData]);

  return (
    <div className='flex flex-row w-full h-full'>
      <div className='flex-center flex-1 items-start w-full overflow-auto h-full'>
        <div className="overflow-auto w-full h-full py-4">
          <div className="flex-center gap-2">
            {
              events.map((event, index) => {
                return (
                  <div key={index} className='w-full'>
                    <button key={index} className='w-full flex flex-col md:flex-row gap-4 p-4 hover:bg-base-200 justify-around items-center' onClick={() => handleRowClick(event)}>
                      <div className='flex flex-col justify-start h-full self-start'>
                        <p className='font-bold'>{String(index + 1).padStart(2, '0')}</p>
                      </div>
                      <div className='aspect-video w-full h-auto md:w-auto md:h-36 border'>
                        <img src={event.coverImage} className='w-full h-full object-cover object-center' alt={`${index + 1} Events`} />
                      </div>
                      <div className='flex flex-col justify-center items-start gap-1 w-full'>
                        <h5 className='text-xl font-bold text-start'>
                          {event.name}
                          <span className={`mx-2 !text-xs font-normal bg-base-300 px-2 py-1 tooltip`} data-tip={eventStatusTooltipMapping[event.status] ?? "???"}>{event.status}</span>
                          {event.isEnded && (
                            <span className="mx-2 !text-xs font-normal bg-error text-error-content px-2 py-1">ENDED</span>
                          )}
                          {event.isTest && (
                            <span className="mx-2 !text-xs font-normal bg-warning text-warning-content px-2 py-1">TEST</span>
                          )}
                        </h5>
                        <div className="flex-1" />
                        <div className='opacity-70 flex flex-row items-center gap-2'>
                          <WarehouseIcon size={16} />
                          <p className='text-sm'>
                            {event?.venue ?? "?"}
                          </p>
                        </div>
                        <div className='opacity-70 flex flex-row items-center gap-2'>
                          <CalendarDaysIcon size={16} />
                          <p className='text-sm'>
                            {!!event.dateTime ? formatFirestoreTimestamp(event.dateTime) : "?"}
                          </p>
                        </div>
                        <div className='opacity-70 flex flex-row items-center gap-2'>
                          <Clock3Icon size={16} />
                          <p className='text-sm'>
                            {!!event.dateTime ? formatFirestoreTimestampToTime(event.dateTime) : "?"}
                          </p>
                        </div>
                      </div>
                      <div className="flex-1" />
                      <div className='hidden md:block p-4'>
                        <ChevronRight />
                      </div>
                    </button>
                    <div className="divider m-0" />
                  </div>
                )
              })
            }
          </div>
          <div className='flex-center w-full p-2'>
            {/* Display messages based on state */}
            {loading && <div className='loading loading-bars' />}
            {!loading && events.length === 0 && <p className='flex-center h-36'>NO RESULT</p>}
            {!loading && events.length > 0 && !hasMore && <p className='opacity-70'>END</p>}
          </div>
          {/* Sentinel element for the Intersection Observer */}
          <div ref={observerRef} style={{ height: '1px' }} />
        </div>
        <div className="divider divider-primary m-0" />
        <div className='w-full'>
          <div className='flex-center flex-row p-4 pt-2 gap-2'>
            <input type="text" className='input input-bordered w-full' placeholder='Search by Name...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <button className='btn btn-primary' onClick={handleCreate}>Create</button>
          </div>
        </div>
      </div>

    </div>
  )
};

export default ManageEvents;
